import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAdminPricing, changeStripePrice } from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, DollarSign, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const centsToUsd = (c) => `$${(c / 100).toFixed(2)}`;

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);

  const load = async () => {
    try {
      setData(await fetchAdminPricing());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link to="/admin" className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100">
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4"><AdminToggle /><UserBadge /></div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-12 pb-24">
        <div className="flex items-center gap-3 mb-4">
          <DollarSign className="h-5 w-5 text-terracotta" />
          <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta">Stripe Pricing</p>
        </div>
        <h1 className="font-serif text-4xl text-bone-100 mb-2">Adjust what new users pay</h1>
        <p className="font-serif italic text-bone-400 mb-4 max-w-2xl">
          Existing subscribers stay grandfathered on their current price forever. Only new checkouts hit
          the new amount. Every change is logged.
        </p>
        <p className="font-sans text-[0.7rem] uppercase tracking-[0.24em] text-terracotta mb-10">
          Live Stripe · not a sandbox
        </p>

        {loading || !data ? (
          <div className="flex items-center gap-3 text-bone-400"><Loader2 className="h-4 w-4 animate-spin" /> loading</div>
        ) : (
          <>
            <div className="space-y-4 mb-16" data-testid="pricing-tiers">
              {data.tiers.map((t) => (
                <div
                  key={t.lookup_key}
                  data-testid={`pricing-tier-${t.lookup_key}`}
                  className="border border-obsidian-600 p-6 flex items-center justify-between gap-4"
                >
                  <div>
                    <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">{t.lookup_key}</p>
                    <p className="font-serif text-2xl text-bone-100 mb-1">
                      {t.label} · {centsToUsd(t.amount_cents)}
                      {t.interval && <span className="text-bone-400 text-lg">/{t.interval}</span>}
                    </p>
                    <p className="font-sans text-xs text-bone-500">
                      {t.active_subscribers} active subscriber{t.active_subscribers === 1 ? "" : "s"} · price_id <code className="text-bone-400">{t.price_id}</code>
                    </p>
                  </div>
                  <Button
                    data-testid={`pricing-edit-${t.lookup_key}`}
                    onClick={() => setEditingTier(t)}
                    className="rounded-none bg-terracotta hover:bg-terracotta-deep text-obsidian-900 font-sans uppercase tracking-wider text-xs px-5 py-4"
                  >
                    Adjust price
                  </Button>
                </div>
              ))}
              {data.tiers.length === 0 && (
                <p className="font-sans text-bone-500 text-sm">No managed tiers found in Stripe.</p>
              )}
            </div>

            <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-4">Recent changes</p>
            {data.recent_events.length === 0 ? (
              <p className="font-sans text-bone-500 text-sm">No changes yet.</p>
            ) : (
              <div className="border border-obsidian-600" data-testid="pricing-events">
                {data.recent_events.map((e, i) => (
                  <div key={i} className="grid grid-cols-4 gap-4 p-4 border-b border-obsidian-600 last:border-b-0">
                    <div className="font-sans text-xs text-bone-400">
                      {new Date(e.changed_at).toLocaleString()}
                    </div>
                    <div className="font-sans text-xs text-terracotta">{e.lookup_key}</div>
                    <div className="font-serif text-sm text-bone-200">
                      {centsToUsd(e.old_amount_cents)} → {centsToUsd(e.new_amount_cents)}
                    </div>
                    <div className="font-sans text-xs text-bone-500 truncate">{e.changed_by}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <PriceChangeModal
        tier={editingTier}
        onClose={() => setEditingTier(null)}
        onSaved={() => { setEditingTier(null); load(); }}
      />
    </div>
  );
}

// ---------- Type-to-confirm modal ----------

function PriceChangeModal({ tier, onClose, onSaved }) {
  const [newDollars, setNewDollars] = useState("");
  const [phrase, setPhrase] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tier) {
      setNewDollars((tier.amount_cents / 100).toFixed(2));
      setPhrase("");
    }
  }, [tier]);

  if (!tier) return null;

  const newCents = Math.round(parseFloat(newDollars || "0") * 100);
  const validAmount = newCents > 0 && newCents !== tier.amount_cents;
  const validPhrase = phrase === "CHANGE PRICE";
  const canSubmit = validAmount && validPhrase && !saving;

  const submit = async () => {
    setSaving(true);
    try {
      await changeStripePrice({
        lookup_key: tier.lookup_key,
        new_amount_cents: newCents,
        confirm_phrase: phrase,
      });
      toast.success("New price is live. Existing subscribers unchanged.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Change failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="pricing-modal"
        className="max-w-lg bg-obsidian-800 border-obsidian-600 rounded-none p-0"
      >
        <VisuallyHidden asChild><DialogTitle>Adjust {tier.label} price</DialogTitle></VisuallyHidden>
        <VisuallyHidden asChild><DialogDescription>Confirm Stripe price change</DialogDescription></VisuallyHidden>
        <div className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="h-4 w-4 text-terracotta" />
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta">Adjust {tier.lookup_key}</p>
          </div>
          <h2 className="font-serif text-2xl text-bone-100 mb-6">{tier.label}</h2>

          <div className="mb-5">
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500 block mb-2">
              Current price
            </label>
            <p className="font-serif text-2xl text-bone-300">
              {centsToUsd(tier.amount_cents)}{tier.interval && `/${tier.interval}`}
            </p>
          </div>

          <div className="mb-5">
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500 block mb-2">
              New price (USD)
            </label>
            <div className="flex items-center gap-2">
              <span className="font-serif text-2xl text-terracotta">$</span>
              <Input
                data-testid="pricing-new-amount"
                type="number"
                step="0.01"
                value={newDollars}
                onChange={(e) => setNewDollars(e.target.value)}
                className="bg-obsidian-900 border-obsidian-600 text-bone-100 rounded-none text-2xl font-serif h-14"
              />
              {tier.interval && <span className="font-serif text-lg text-bone-400">/{tier.interval}</span>}
            </div>
          </div>

          <div className="border border-terracotta/60 p-4 mb-5">
            <p className="font-sans text-xs text-bone-200 leading-relaxed">
              <strong className="text-terracotta">{tier.active_subscribers} existing subscriber{tier.active_subscribers === 1 ? "" : "s"}</strong> stay
              at {centsToUsd(tier.amount_cents)} forever. New checkouts will pay{" "}
              <strong className="text-terracotta">{validAmount ? centsToUsd(newCents) : "—"}</strong> immediately.
            </p>
          </div>

          <div className="mb-6">
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500 block mb-2">
              Type <code className="text-terracotta">CHANGE PRICE</code> to confirm
            </label>
            <Input
              data-testid="pricing-confirm-input"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder="CHANGE PRICE"
              className="bg-obsidian-900 border-obsidian-600 text-bone-100 rounded-none font-mono"
              autoComplete="off"
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <button
              onClick={onClose}
              className="font-sans text-xs uppercase tracking-[0.2em] text-bone-400 hover:text-bone-100"
            >
              Cancel
            </button>
            <Button
              data-testid="pricing-submit-btn"
              onClick={submit}
              disabled={!canSubmit}
              className="rounded-none bg-terracotta hover:bg-terracotta-deep text-obsidian-900 font-sans uppercase tracking-wider text-xs px-6 py-5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Change price
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
